import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [inviter, setInviter] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", referral_code: "", country: "IQ" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 1. Try URL query param ?ref=CODE first
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get("ref");
    if (urlCode) {
      const code = urlCode.toUpperCase();
      sessionStorage.setItem("pending_ref_code", code);
      // Lookup inviter name asynchronously
      (async () => {
        try {
          const api = (await import("../lib/api")).default;
          const { data } = await api.get(`/referral/lookup?code=${encodeURIComponent(code)}`);
          if (data.valid) {
            sessionStorage.setItem("pending_ref_inviter", data.inviter_name);
            setInviter(data.inviter_name);
          }
        } catch { /* ignore */ }
      })();
      setForm((f) => ({ ...f, referral_code: code }));
      return;
    }
    // 2. Fallback to sessionStorage (set by /ref/:code route)
    const code = sessionStorage.getItem("pending_ref_code");
    const inv  = sessionStorage.getItem("pending_ref_inviter");
    if (code) {
      setForm((f) => ({ ...f, referral_code: code }));
      setInviter(inv);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const api = (await import("../lib/api")).default;
      const { data } = await api.post("/auth/register", {
        email: form.email, password: form.password, name: form.name,
        phone: form.phone || null,
        referral_code: form.referral_code || null,
        country: form.country,
      });
      localStorage.setItem("sulfa_token", data.token);
      sessionStorage.removeItem("pending_ref_code");
      sessionStorage.removeItem("pending_ref_inviter");
      if (form.referral_code) toast.success("🎉 مبروك! حصلت على مكافأة ترحيبية");
      else toast.success(t("register") + " ✓");
      window.location.href = "/kyc";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Register failed");
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="text-xs text-[#8FA3BD] mb-6 inline-block hover:text-[#00E5FF]" data-testid="back-home-link">← {t("backToHome")}</Link>
        <div className="bento-card p-8 animate-fade-up">
          <h1 className="text-3xl font-bold mb-2 text-cyan-gradient">{t("register")}</h1>
          <p className="text-sm text-[#8FA3BD] mb-4">{t("appName")}</p>
          {inviter && (
            <div className="mb-6 p-3 rounded-lg bg-[#00E676]/10 border border-[#00E676]/40 text-sm text-center" data-testid="inviter-banner">
              🎁 تمت دعوتك بواسطة <span className="text-[#00E5FF] font-bold">{inviter}</span> — ستحصل على 5% بونص على أول إيداع
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("name")}</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white mt-1.5" data-testid="register-name-input" />
            </div>
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("email")}</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white mt-1.5" data-testid="register-email-input" />
            </div>
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white mt-1.5" data-testid="register-phone-input" placeholder="+9647xxxxxxxxx" />
            </div>
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("country") || "Country"}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button type="button" onClick={() => setForm({ ...form, country: "IQ" })}
                  className={`p-3 rounded-lg border text-sm transition ${form.country === "IQ" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
                  data-testid="register-country-iq">🇮🇶 العراق</button>
                <button type="button" onClick={() => setForm({ ...form, country: "INTL" })}
                  className={`p-3 rounded-lg border text-sm transition ${form.country === "INTL" ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-[#1A2D4D] text-[#8FA3BD]"}`}
                  data-testid="register-country-intl">🌍 International</button>
              </div>
              <p className="text-xs text-[#4A5970] mt-1.5">
                {form.country === "IQ" ? "💳 Zain Cash + Super Qi" : "💎 USDT + TRX"}
              </p>
            </div>
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("password")}</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#050E1A] border-[#1A2D4D] text-white mt-1.5" data-testid="register-password-input" />
            </div>
            <div>
              <Label className="text-[#8FA3BD] text-xs">{t("refCode")}</Label>
              <Input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} className="bg-[#050E1A] border-[#1A2D4D] text-white mt-1.5" data-testid="register-ref-input" />
            </div>
            <Button type="submit" disabled={loading} className="btn-cyan w-full mt-2" data-testid="register-submit-btn">
              {loading ? "..." : t("submit")}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1A2D4D]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#0F1F35] px-3 text-[#4A5970]">{t("or")}</span></div>
          </div>

          <Button onClick={handleGoogle} variant="outline" className="w-full border-[#1A2D4D] text-white hover:bg-[#1A2D4D]" data-testid="google-register-btn">
            {t("continueGoogle")}
          </Button>

          <p className="text-center text-xs text-[#8FA3BD] mt-6">
            <Link to="/login" className="text-[#00E5FF]" data-testid="goto-login-link">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
